import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { glyphGroup, type GlyphId } from '../../../components/glyphs';
import { draw, pulse } from '../../../components/svg';
import { words, revealWords, settleWords } from '../../../components/text';

/** One action and the signals captured with it (CARBONMOVE.md §5, §14 of Prompt2). Values are illustrative. */
const ACTION = { x: 190, y: 430 };
const SIGNALS: ReadonlyArray<{ label: string; glyph: GlyphId; raw: string }> = [
  { label: 'GPS', glyph: 'pin', raw: '6.9271°N 79.8612°E' },
  { label: 'Motion', glyph: 'motion', raw: 'cadence 82 rpm' },
  { label: 'Time', glyph: 'clock', raw: '07:42 → 08:06' },
  { label: 'Context', glyph: 'layers', raw: 'route · weather' },
];
const SIGNAL_X = 480;
const signalY = (i: number): number => 250 + i * 120;
const PROOF = { x: 800, y: 430 };
const VERIFY = { x: 1060, y: 430, r: 64 };
const IMPACT = { x: 1330, y: 360 };
const REWARD = { x: 1330, y: 540 };
const VALIDATORS = [0, 72, 144, 216, 288].map((deg) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: VERIFY.x + Math.cos(a) * VERIFY.r, y: VERIFY.y + Math.sin(a) * VERIFY.r };
});
const RECORD_Y = 760;

/**
 * 0 One action, many signals — ONE ACTION. MANY SIGNALS.
 * 1 The signals become a proof; raw values stay private — PROVE THE ACTION, NOT THE RAW DATA.
 * 2 Independent checks agree — VERIFIED
 * 3 A measurable impact, a reward, and a lasting record
 * 4 CLIMATE ACTION YOU CAN VERIFY.
 */
export class CarbonVerifyScene extends BaseScene {
  public readonly stepCount = 5;
  private flowing = false;

  protected template(): string {
    const signals = SIGNALS.map((s, i) => {
      const y = signalY(i);
      return `<g class="cv-signal" data-index="${i}">
        <path class="cv-capture draw" pathLength="1" d="M${ACTION.x + 74} ${ACTION.y} C${ACTION.x + 170} ${ACTION.y} ${SIGNAL_X - 170} ${y} ${SIGNAL_X - 96} ${y}"/>
        <path class="cv-merge draw" pathLength="1" d="M${SIGNAL_X + 96} ${y} C${SIGNAL_X + 190} ${y} ${PROOF.x - 150} ${PROOF.y} ${PROOF.x - 58} ${PROOF.y}"/>
        <g class="cv-chip">
          <rect x="${SIGNAL_X - 96}" y="${y - 30}" width="192" height="60" rx="14"/>
          <g transform="translate(${SIGNAL_X - 66} ${y})">${glyphGroup(s.glyph, 24)}</g>
          <text class="cv-chip__label" x="${SIGNAL_X - 42}" y="${y - 4}">${s.label.toUpperCase()}</text>
          <text class="cv-chip__raw" x="${SIGNAL_X - 42}" y="${y + 17}">${s.raw}</text>
          <text class="cv-chip__masked" x="${SIGNAL_X - 42}" y="${y + 17}">• • • • • •</text>
        </g>
        <circle class="cv-pulse" r="4"/>
      </g>`;
    }).join('');

    const validators = VALIDATORS.map((v, i) => {
      const n = VALIDATORS[(i + 2) % VALIDATORS.length];
      return `<path class="cv-check" d="M${v.x.toFixed(1)} ${v.y.toFixed(1)} L${n.x.toFixed(1)} ${n.y.toFixed(1)}"/>`;
    }).join('') + VALIDATORS.map((v) => `<circle class="cv-validator" cx="${v.x.toFixed(1)}" cy="${v.y.toFixed(1)}" r="9"/>`).join('');

    const records = Array.from({ length: 9 }, (_, i) => `<rect class="cv-record" data-index="${i}" x="${560 + i * 64}" y="${RECORD_Y - 16}" width="44" height="32" rx="5"/>`).join('');

    return `
      <div class="scene-layout carbon-verify">
        <svg class="diagram cv-diagram" viewBox="0 0 1600 900" role="img" aria-label="A cycling trip captured as signals, turned into a private proof, verified, measured and rewarded">
          <g transform="translate(${ACTION.x} ${ACTION.y})"><g class="cv-action">
            <circle class="cv-action__ring" r="74"/>
            <g class="cv-action__glyph">${glyphGroup('bike', 62)}</g>
            <text class="cv-action__label" y="118" text-anchor="middle">CYCLING</text>
          </g></g>
          ${signals}
          <g class="cv-proof" data-from="1">
            <path class="cv-proof__hex" d="${hexagon(PROOF.x, PROOF.y, 58)}"/>
            <text class="cv-proof__mark" x="${PROOF.x}" y="${PROOF.y + 12}" text-anchor="middle">#</text>
            <text class="cv-node__label" x="${PROOF.x}" y="${PROOF.y + 100}" text-anchor="middle">PROOF</text>
          </g>
          <g class="cv-verify" data-from="2">
            <path class="cv-link draw" pathLength="1" d="M${PROOF.x + 62} ${PROOF.y} H${VERIFY.x - VERIFY.r - 16}"/>
            ${validators}
            <path class="cv-tick draw" pathLength="1" d="M${VERIFY.x - 22} ${VERIFY.y + 2} L${VERIFY.x - 6} ${VERIFY.y + 18} L${VERIFY.x + 24} ${VERIFY.y - 16}"/>
            <text class="cv-node__label cv-verified" x="${VERIFY.x}" y="${VERIFY.y + 106}" text-anchor="middle">VERIFIED</text>
          </g>
          <g class="cv-outcome" data-from="3">
            <path class="cv-link draw" pathLength="1" d="M${VERIFY.x + VERIFY.r + 16} ${VERIFY.y - 10} C${VERIFY.x + 140} ${VERIFY.y - 10} ${IMPACT.x - 120} ${IMPACT.y} ${IMPACT.x - 50} ${IMPACT.y}"/>
            <path class="cv-link draw" pathLength="1" d="M${VERIFY.x + VERIFY.r + 16} ${VERIFY.y + 10} C${VERIFY.x + 140} ${VERIFY.y + 10} ${REWARD.x - 120} ${REWARD.y} ${REWARD.x - 42} ${REWARD.y}"/>
            <g class="cv-impact">
              <circle class="cv-impact__ring" cx="${IMPACT.x}" cy="${IMPACT.y}" r="44"/>
              <text class="cv-impact__co2" x="${IMPACT.x}" y="${IMPACT.y + 8}" text-anchor="middle">CO₂</text>
              <text class="cv-node__label" x="${IMPACT.x + 64}" y="${IMPACT.y + 8}">MEASURABLE IMPACT</text>
            </g>
            <g transform="translate(${REWARD.x} ${REWARD.y})"><g class="cv-reward">
              <circle class="cv-reward__coin" r="34"/>
              <circle class="cv-reward__inner" r="22"/>
              <text class="cv-node__label" x="56" y="8">REWARD</text>
            </g></g>
            <g class="cv-records">
              <text class="cv-records__label" x="530" y="${RECORD_Y + 6}" text-anchor="end">RECORDED</text>
              ${records}
            </g>
          </g>
        </svg>
        <div class="cv-statements">
          <h2 class="statement" data-from="0" data-until="0">${words('ONE ACTION. MANY SIGNALS.')}</h2>
          <h2 class="statement statement--accent" data-from="1" data-until="1">${words('PROVE THE ACTION, NOT THE RAW DATA.')}</h2>
          <div class="cv-final" data-from="4">
            <span class="cv-final__steps">COMMIT · VERIFY · REWARD</span>
            <h2 class="statement statement--accent">${words('CLIMATE ACTION YOU CAN VERIFY.')}</h2>
          </div>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$('.cv-action'), { opacity: 0, scale: 0.7, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 0.9, ease: 'back.out(1.8)' }, 0.1);
    const captures = this.$$('.cv-capture');
    draw(captures, false, { immediate: true });
    tl.add(draw(captures, true, { duration: 0.8, stagger: 0.12 }), 0.6);
    tl.fromTo(this.$$('.cv-chip'), { opacity: 0, x: -16 }, { opacity: 1, x: 0, duration: 0.6, stagger: 0.12, ease: 'expo.out' }, 0.9);
    tl.add(revealWords(this.$('.cv-statements .statement')), 1.4);
  }

  protected onExit(): void {
    this.flowing = false;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    gsap.set(this.$('.cv-action'), { opacity: 1, scale: 1 });
    this.$$('.cv-statements .statement').forEach((s) => settleWords(s));
    if (forward && step === 1) tl.add(revealWords(this.$$('.cv-statements .statement')[1]), 0.2);
    this.$('.cv-diagram').dataset.private = String(step >= 1);

    // 0 — the trip is captured as four signals.
    const captures = this.$$('.cv-capture');
    const chips = this.$$('.cv-chip');
    draw(captures, true, { immediate: true });
    gsap.set(chips, { opacity: 1, x: 0 });

    // 1 — the signals merge into a proof. Their raw values are never passed on.
    const merges = this.$$('.cv-merge');
    const proof = this.$$('.cv-proof path, .cv-proof text');
    if (forward && step === 1) {
      draw(merges, false, { immediate: true });
      tl.add(draw(merges, true, { duration: 0.9, stagger: 0.1 }), 0.4);
      tl.fromTo(proof, { opacity: 0, scale: 0.6, svgOrigin: `${PROOF.x} ${PROOF.y}` }, { opacity: 1, scale: 1, duration: 0.8, ease: 'back.out(2)' }, 1.1);
    } else {
      draw(merges, step >= 1, { immediate: true });
      gsap.set(proof, { opacity: 1, scale: 1, svgOrigin: `${PROOF.x} ${PROOF.y}` });
    }

    // 2 — independent validators cross-check, then agree.
    const links = this.$$('.cv-verify .cv-link');
    const checks = this.$$('.cv-check, .cv-validator');
    const tick = this.$('.cv-tick');
    const verified = this.$('.cv-verified');
    if (forward && step === 2) {
      draw([...links, tick], false, { immediate: true });
      tl.add(draw(links, true, { duration: 0.6 }), 0.2);
      tl.fromTo(checks, { opacity: 0 }, { opacity: 1, duration: 0.3, stagger: 0.08 }, 0.6);
      tl.add(draw(tick, true, { duration: 0.5, ease: 'power2.out' }), 1.6);
      tl.fromTo(verified, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.5 }, 1.9);
    } else {
      draw([...links, tick], step >= 2, { immediate: true });
      gsap.set(checks, { opacity: 1 });
      gsap.set(verified, { opacity: 1, y: 0 });
    }

    // 3 — impact is measured, a reward follows, and the result is recorded.
    const outLinks = this.$$('.cv-outcome .cv-link');
    const outcome = this.$$('.cv-impact, .cv-reward');
    const records = this.$$('.cv-record, .cv-records__label');
    if (forward && step === 3) {
      draw(outLinks, false, { immediate: true });
      tl.add(draw(outLinks, true, { duration: 0.7, stagger: 0.2 }), 0.2);
      tl.fromTo(outcome, { opacity: 0, scale: 0.7, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 0.7, stagger: 0.35, ease: 'back.out(2)' }, 0.8);
      tl.fromTo(records, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, stagger: 0.06 }, 1.6);
    } else {
      draw(outLinks, step >= 3, { immediate: true });
      gsap.set(outcome, { opacity: 1, scale: 1 });
      gsap.set(records, { opacity: 1, y: 0 });
    }

    gsap.to(this.$('.cv-diagram'), { opacity: step >= 4 ? 0.16 : 1, duration: d * 0.9, ease: 'power2.inOut' });
    if (forward && step === 4) {
      tl.fromTo(this.$('.cv-final__steps'), { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.6 }, 0.2);
      tl.add(revealWords(this.$('.cv-final .statement')), 0.5);
    } else {
      gsap.set(this.$('.cv-final__steps'), { opacity: 1, y: 0 });
    }

    this.syncFlow(step >= 1 && step <= 3, forward && step === 1 ? 1.4 : 0);
  }

  private syncFlow(on: boolean, delay: number): void {
    if (on === this.flowing) return;
    this.flowing = on;
    this.stopLoops();
    const markers = this.$$<SVGCircleElement>('.cv-pulse');
    markers.forEach((m) => { m.style.opacity = '0'; });
    if (!on) return;
    const paths = this.$$<SVGPathElement>('.cv-merge');
    markers.forEach((m, i) => this.loop(pulse(m, paths[i], { duration: 1.2, delay: delay + i * 0.25, repeatDelay: 1 })));
  }
}

function hexagon(cx: number, cy: number, r: number): string {
  return Array.from({ length: 6 }, (_, i) => {
    const a = ((60 * i - 90) * Math.PI) / 180;
    return `${i ? 'L' : 'M'}${(cx + Math.cos(a) * r).toFixed(1)} ${(cy + Math.sin(a) * r).toFixed(1)}`;
  }).join(' ') + ' Z';
}
